import { Tooltip, Typography } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Horizontal } from '@gravitate-js/excalibrr'
import type { FormInstance } from 'antd'
import type { ConfigField } from '../utils/configTypes'
import { CONFIG_COLUMN_WIDTH } from '../utils/configTypes'
import { ConfigSwitch } from './ConfigSwitch'

const { Text } = Typography

export function getConfigTableColumns(form: FormInstance, onFormChange?: () => void) {
  return [
    {
      title: 'Setting',
      dataIndex: 'name',
      key: 'name',
      className: 'setting-column',
      width: '40%',
      render: (text: string, record: ConfigField) => {
        if (record.isSection) {
          return (
            <Text strong className='section-header'>
              {text}
            </Text>
          )
        }
        return (
          <Horizontal gap={8} verticalCenter style={{ padding: '4px 0' }}>
            <div className='setting-name'>{text}</div>
            {record.description && (
              <Tooltip title={record.description}>
                <InfoCircleOutlined className='info-icon' />
              </Tooltip>
            )}
          </Horizontal>
        )
      },
    },
    {
      title: (
        <Horizontal gap={4} justifyContent='center' alignItems='center'>
          <span>Match</span>
          <Tooltip title='All enabled criteria must be met for a successful match.'>
            <InfoCircleOutlined style={{ fontSize: 14, color: '#8c8c8c' }} />
          </Tooltip>
        </Horizontal>
      ),
      key: 'match',
      className: 'match-column',
      width: CONFIG_COLUMN_WIDTH,
      align: 'center' as const,
      render: (_: unknown, record: ConfigField) => {
        if (record.isSection) return null
        if (!record.columns.match) return <span className='not-applicable'>—</span>

        return (
          <ConfigSwitch
            form={form}
            configType='auto_match_rules'
            field={record}
            alwaysEnabled={record.columns.match === 'always'}
            onFormChange={onFormChange}
          />
        )
      },
    },
    {
      title: (
        <Horizontal gap={4} justifyContent='center' alignItems='center'>
          <span>Auto Approve</span>
          <Tooltip title='All enabled criteria must be met for automatic approval.'>
            <InfoCircleOutlined style={{ fontSize: 14, color: '#8c8c8c' }} />
          </Tooltip>
        </Horizontal>
      ),
      key: 'approve',
      className: 'approve-column',
      width: CONFIG_COLUMN_WIDTH,
      align: 'center' as const,
      render: (_: unknown, record: ConfigField) => {
        if (record.isSection) return null
        if (!record.columns.approve) return <span className='not-applicable'>—</span>

        return <ConfigSwitch form={form} configType='auto_approve_rules' field={record} onFormChange={onFormChange} />
      },
    },
    {
      title: (
        <Horizontal gap={4} justifyContent='center' alignItems='center'>
          <span>Warning</span>
          <Tooltip title='Each toggle triggers its own independent warning.'>
            <InfoCircleOutlined style={{ fontSize: 14, color: '#8c8c8c' }} />
          </Tooltip>
        </Horizontal>
      ),
      key: 'warning',
      className: 'warning-column',
      width: CONFIG_COLUMN_WIDTH,
      align: 'center' as const,
      render: (_: unknown, record: ConfigField) => {
        if (record.isSection) return null
        if (!record.columns.warning) return <span className='not-applicable'>—</span>

        return <ConfigSwitch form={form} configType='warning_rules' field={record} onFormChange={onFormChange} />
      },
    },
  ]
}
